import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, HStack, IconButton, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Spinner, Text, Tooltip } from "@chakra-ui/react";
import { HiOutlinePause, HiOutlinePlay, HiOutlineStop } from "react-icons/hi2";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  const ss = s % 60;
  return hh > 0
    ? `${hh}:${mm.toString().padStart(2, "0")}:${ss.toString().padStart(2, "0")}`
    : `${mm.toString().padStart(2, "0")}:${ss.toString().padStart(2, "0")}`;
};

/**
 * Lightweight, custom audio player that plays a blob returned by `getBlob`.
 * Keeps UI consistent and avoids bypassing axios auth interceptors.
 */
const InlineAudioPlayer = ({
  getBlob,
  isDisabled = false,
  disabledReason = "",
  onAttemptWhileDisabled,
  size = "xs",
}) => {
  const audioRef = useRef(null);
  const isSeekingRef = useRef(false);

  const [objectUrl, setObjectUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const tooltipLabel = useMemo(() => {
    if (!isDisabled) return "";
    return disabledReason || "Unavailable";
  }, [isDisabled, disabledReason]);

  const attemptWhileDisabled = useCallback(() => {
    onAttemptWhileDisabled?.();
  }, [onAttemptWhileDisabled]);

  const ensureLoaded = useCallback(async () => {
    if (objectUrl) return;
    setIsLoading(true);
    try {
      const blob = await getBlob();
      const url = URL.createObjectURL(blob);
      setObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } finally {
      setIsLoading(false);
    }
  }, [getBlob, objectUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handleLoaded = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    const handleTimeUpdate = () => {
      if (isSeekingRef.current) return;
      setCurrentTime(Number.isFinite(audio.currentTime) ? audio.currentTime : 0);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(Number.isFinite(audio.duration) ? audio.duration : 0);
    };

    audio.addEventListener("loadedmetadata", handleLoaded);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoaded);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !objectUrl) return;
    audio.src = objectUrl;
    audio.load();
  }, [objectUrl]);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const playOrPause = async () => {
    if (isDisabled) {
      attemptWhileDisabled();
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      return;
    }

    try {
      await ensureLoaded();
      await audio.play();
    } catch {
      // Ignore play() promise rejections (e.g. user gesture requirements)
    }
  };

  const stop = () => {
    if (isDisabled) {
      attemptWhileDisabled();
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
      setCurrentTime(0);
    } catch {
      // ignore
    }
  };

  const onSeekStart = () => {
    isSeekingRef.current = true;
  };

  const onSeek = (v) => {
    setCurrentTime(v);
  };

  const onSeekEnd = (v) => {
    isSeekingRef.current = false;
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.currentTime = clamp(v, 0, duration || 0);
    } catch {
      // ignore
    }
  };

  const controls = (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor={isDisabled ? "gray.100" : "gray.200"}
      borderRadius="lg"
      px={3}
      py={2}
      boxShadow="sm"
      transition="all 120ms ease"
      _hover={isDisabled ? undefined : { boxShadow: "md", borderColor: "gray.300" }}
      opacity={isDisabled ? 0.6 : 1}
      cursor={isDisabled ? "not-allowed" : "default"}
      onClickCapture={isDisabled ? attemptWhileDisabled : undefined}
    >
      <HStack spacing={2.5} align="center">
      <audio ref={audioRef} style={{ display: "none" }} />

      <IconButton
        icon={isLoading ? <Spinner size="xs" /> : isPlaying ? <HiOutlinePause size={14} /> : <HiOutlinePlay size={14} />}
        size={size}
        colorScheme="blue"
        variant={isDisabled ? "ghost" : "solid"}
        borderRadius="md"
        aria-label={isPlaying ? "Pause" : "Play"}
        onClick={playOrPause}
        isDisabled={isLoading}
        aria-disabled={isDisabled}
        opacity={isDisabled ? 0.55 : 1}
      />

      <IconButton
        icon={<HiOutlineStop size={14} />}
        size={size}
        colorScheme="gray"
        variant="ghost"
        borderRadius="md"
        aria-label="Stop"
        onClick={stop}
        isDisabled={isLoading}
        aria-disabled={isDisabled}
        opacity={isDisabled ? 0.55 : 1}
      />

      <HStack spacing={2} minW="240px">
        <Text fontSize="xs" color="gray.600" fontFamily="mono" minW="86px">
          {formatTime(currentTime)} / {formatTime(duration)}
        </Text>
        <Slider
          aria-label="Seek"
          size="sm"
          min={0}
          max={duration || 0}
          step={0.1}
          value={duration ? currentTime : 0}
          onChangeStart={onSeekStart}
          onChange={onSeek}
          onChangeEnd={onSeekEnd}
          isDisabled={isDisabled || !duration || isLoading}
        >
          <SliderTrack bg="gray.100" borderRadius="full">
            <SliderFilledTrack bg="blue.500" borderRadius="full" />
          </SliderTrack>
          <SliderThumb boxSize={3} borderWidth="2px" borderColor="blue.500" />
        </Slider>
      </HStack>
      </HStack>
    </Box>
  );

  if (!isDisabled || !tooltipLabel) return controls;

  return (
    <Tooltip label={tooltipLabel} hasArrow placement="top">
      {controls}
    </Tooltip>
  );
};

export default InlineAudioPlayer;

